import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API, useAuth } from '../../App';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import ImageUploader from '../../components/ImageUploader';
import { Plus, Pencil, Trash2, MapPin, Phone, Clock, RefreshCw, Building2, Heart, Shield, GraduationCap, AlertTriangle, Truck } from 'lucide-react';

const categories = [
  { value: 'clinic', label: 'Medical/Clinic', icon: Heart },
  { value: 'police', label: 'Police', icon: Shield },
  { value: 'school', label: 'School', icon: GraduationCap },
  { value: 'shelter', label: 'Shelter', icon: AlertTriangle },
  { value: 'service', label: 'Service', icon: Truck }
];

const AdminResources = () => {
  const { token } = useAuth();
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({
    name: '', category: 'service', address: '', phone: '', hours: '', image_url: ''
  });

  const fetchResources = async () => {
    try {
      const response = await axios.get(`${API}/resources`);
      setResources(response.data);
    } catch (error) {
      console.error('Failed to fetch:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchResources(); }, []);

  const handleSubmit = async () => {
    if (!formData.name || !formData.address) {
      toast.error('Please fill in required fields');
      return;
    }

    try {
      if (editing) {
        await axios.put(`${API}/resources/${editing}`, formData, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Resource updated!');
      } else {
        await axios.post(`${API}/resources`, formData, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Resource added!');
      }
      setShowDialog(false);
      setFormData({ name: '', category: 'service', address: '', phone: '', hours: '', image_url: '' });
      setEditing(null);
      fetchResources();
    } catch (error) {
      toast.error('Failed to save');
    }
  };

  const handleEdit = (item) => {
    setFormData({ name: item.name, category: item.category, address: item.address, phone: item.phone || '', hours: item.hours || '', image_url: item.image_url || '' });
    setEditing(item.id);
    setShowDialog(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this resource?')) return;
    try {
      await axios.delete(`${API}/resources/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Deleted!');
      fetchResources();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const getCategoryIcon = (cat) => {
    const found = categories.find(c => c.value === cat);
    return found ? found.icon : Building2;
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Building2 className="w-6 h-6 text-purple-500" /> Resources & Services
          </h1>
          <p className="text-slate-600">Manage local services, clinics, schools, and emergency info</p>
        </div>
        <Dialog open={showDialog} onOpenChange={(open) => {
          setShowDialog(open);
          if (!open) { setEditing(null); setFormData({ name: '', category: 'service', address: '', phone: '', hours: '', image_url: '' }); }
        }}>
          <DialogTrigger asChild>
            <Button className="bg-plp-blue hover:bg-plp-blue-light"><Plus className="w-4 h-4 mr-2" /> Add Resource</Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>{editing ? 'Edit Resource' : 'Add Resource'}</DialogTitle></DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <Label>Category</Label>
                <Select value={formData.category} onValueChange={(v) => setFormData(p => ({ ...p, category: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{categories.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Name *</Label><Input value={formData.name} onChange={(e) => setFormData(p => ({ ...p, name: e.target.value }))} placeholder="Resource name" /></div>
              <div><Label>Address *</Label><Input value={formData.address} onChange={(e) => setFormData(p => ({ ...p, address: e.target.value }))} placeholder="Full address" /></div>
              <div><Label>Phone</Label><Input value={formData.phone} onChange={(e) => setFormData(p => ({ ...p, phone: e.target.value }))} placeholder="242-XXX-XXXX" /></div>
              <div><Label>Hours</Label><Input value={formData.hours} onChange={(e) => setFormData(p => ({ ...p, hours: e.target.value }))} placeholder="Mon-Fri 9am-5pm" /></div>
              <ImageUploader
                label="Image (optional)"
                value={formData.image_url}
                onChange={(url) => setFormData(p => ({ ...p, image_url: url }))}
                placeholder="https://example.com/image.jpg"
              />
              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setShowDialog(false)} className="flex-1">Cancel</Button>
                <Button onClick={handleSubmit} className="flex-1 bg-plp-blue">{editing ? 'Update' : 'Add'}</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><RefreshCw className="w-8 h-8 animate-spin text-plp-blue" /></div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {resources.map((item) => {
            const Icon = getCategoryIcon(item.category);
            return (
              <Card key={item.id} className="bg-white">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex gap-3">
                      <div className="w-10 h-10 bg-plp-blue/10 rounded-lg flex items-center justify-center">
                        <Icon className="w-5 h-5 text-plp-blue" />
                      </div>
                      <div>
                        <Badge variant="outline" className="mb-1 capitalize text-xs">{item.category}</Badge>
                        <h3 className="font-heading font-semibold">{item.name}</h3>
                        <div className="mt-2 space-y-1 text-xs text-slate-500">
                          <p className="flex items-center gap-1"><MapPin className="w-3 h-3" />{item.address}</p>
                          {item.phone && <p className="flex items-center gap-1"><Phone className="w-3 h-3" />{item.phone}</p>}
                          {item.hours && <p className="flex items-center gap-1"><Clock className="w-3 h-3" />{item.hours}</p>}
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-col gap-1">
                      <Button variant="ghost" size="icon" onClick={() => handleEdit(item)}><Pencil className="w-4 h-4" /></Button>
                      <Button variant="ghost" size="icon" onClick={() => handleDelete(item.id)} className="text-red-500"><Trash2 className="w-4 h-4" /></Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AdminResources;
